import { PartialXMLStreamParser } from "partial-xml-stream-parser";

/**
 * StreamBlockParser - Extracts all blocks from streaming text in order
 * Now backed by partial-xml-stream-parser to support the new agent spec.
 *
 * Handles the following format (see agent_spec.md):
 * <meta_init ...></meta_init>
 * <thinking>...</thinking>
 * normal tokens...
 * <tool_call id name arguments></tool_call>
 * <tool_result id name><![CDATA[result_text]]></tool_result>
 * <citation id>inline text</citation>
 * <excel-citation ...>inline text</excel-citation>
 * <chart>{"type": "bar", "data": {...}, "options": {...}}</chart>
 * <meta_final ...></meta_final>
 * <max_steps_reached steps limit></max_steps_reached>
 */

class StreamBlockParser {
  /**
   * @param {Object} [options]
   * @param {(block: ParsedBlock) => void} [options.onBlock]
   * @param {(error: Error) => void} [options.onError]
   */
  constructor(options = {}) {
    this.onBlock = options.onBlock || null;
    this.onError = options.onError || null;
    this.blocks = [];
  }

  /**
   * Parse complete text at once (non-streaming)
   * @param {string} text
   * @returns {Array<ParsedBlock>}
   */
  static parseComplete(text) {
    const parser = new StreamBlockParser();
    try {
      return parser._parseTextToBlocks(text || "");
    } catch (error) {
      if (parser.onError) parser.onError(error);
      return [];
    }
  }

  /**
   * Transform full text using PartialXMLStreamParser into UI blocks.
   * @private
   */
  _parseTextToBlocks(text) {
    // Pre-process chart tags manually before XML parsing
    // Chart content is JSON and would break XML parser due to special characters
    const chartBlocks = [];
    let processedText = text;

    // Check for incomplete chart tags and remove them from processing
    const lastChartOpenIdx = text.lastIndexOf("<chart>");
    const lastChartCloseIdx = text.lastIndexOf("</chart>");
    if (lastChartOpenIdx !== -1 && lastChartOpenIdx > lastChartCloseIdx) {
      // There's an unclosed chart tag - remove everything from <chart> onwards
      processedText = text.substring(0, lastChartOpenIdx);
    }

    // Extract all complete chart blocks and replace them with placeholders
    const chartRegex = /<chart>([\s\S]*?)<\/chart>/g;
    let match;
    let chartIndex = 0;

    while ((match = chartRegex.exec(processedText)) !== null) {
      const chartJson = match[1].trim();
      const placeholder = `__CHART_PLACEHOLDER_${chartIndex}__`;

      // Try to parse the JSON config
      let config = null;
      try {
        config = JSON.parse(chartJson);
      } catch (error) {
        console.error("[StreamBlockParser] Failed to parse chart JSON:", error);
        // Store raw JSON string if parse fails
        config = chartJson;
      }

      chartBlocks.push({
        placeholder,
        config,
        position: match.index,
      });

      chartIndex++;
    }

    // Replace chart tags with placeholders for XML parsing
    processedText = processedText.replace(
      chartRegex,
      (match, content, offset) => {
        const idx = chartBlocks.findIndex((cb) => cb.position === offset);
        return idx !== -1 ? chartBlocks[idx].placeholder : match;
      }
    );

    const p = new PartialXMLStreamParser({
      allowedRootNodes: [
        "meta_init",
        "meta_final",
        "max_steps_reached",
        "thinking",
        "tool_call",
        "tool_result",
        "citation",
        "excel-citation",
        "entity",
        "file",
        "IsolationChecklist",
      ],
      stopNodes: ["citation", "excel-citation"],
      alwaysCreateTextNode: true,
      textNodeName: "#text",
    });

    // Single-shot parse
    const result = p.parseStream(processedText);
    p.parseStream(null); // finalize

    const tokens = (result && result.xml) || [];
    const blocks = [];
    let textBuffer = "";

    const flushText = () => {
      const cleaned = sanitizeControlTagLeak(textBuffer);

      // Check for chart placeholders in text and convert to chart blocks
      const placeholderRegex = /__CHART_PLACEHOLDER_(\d+)__/g;
      let lastIndex = 0;
      let textMatch;

      while ((textMatch = placeholderRegex.exec(cleaned)) !== null) {
        // Add text before placeholder
        const beforeText = cleaned.substring(lastIndex, textMatch.index);
        if (beforeText && beforeText.replace(/[\t\n\r ]/g, "").length > 0) {
          blocks.push({ type: "text", content: beforeText });
        }

        // Add chart block
        const chartIdx = parseInt(textMatch[1], 10);
        const chartBlock = chartBlocks[chartIdx];
        if (chartBlock) {
          blocks.push({
            type: "chart",
            config: chartBlock.config,
            content: null,
            selfClosing: false,
          });
        }

        lastIndex = textMatch.index + textMatch[0].length;
      }

      // Add remaining text after last placeholder
      const remainingText = cleaned.substring(lastIndex);
      if (remainingText && remainingText.replace(/[\t\n\r ]/g, "").length > 0) {
        blocks.push({ type: "text", content: remainingText });
      }

      textBuffer = "";
    };

    const xmlUnescape = (s) => {
      if (typeof s !== "string") return s;
      return s
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&");
    };

    const getAttrs = (node) => {
      const attrs = {};
      Object.keys(node || {}).forEach((k) => {
        if (k.startsWith("@")) {
          attrs[k.slice(1)] = node[k];
        }
      });
      return attrs;
    };

    const CONTROL_TAGS = [
      "meta_init",
      "meta_final",
      "max_steps_reached",
      "tool_call",
      "tool_result",
      "thinking",
      "chart",
      "file",
      "IsolationChecklist",
    ];

    const sanitizeControlTagLeak = (str) => {
      if (!str) return str;
      // Remove any complete or partial occurrences of control tags that should never be shown as raw text
      const names = CONTROL_TAGS.join("|");
      // 1) Remove complete open/close tags
      const reComplete = new RegExp(`<\\/?(?:${names})(?:[^>]*)?>`, "gi");
      let out = str.replace(reComplete, "");
      // 2) Remove dangling partial starts like "<tool_call id=..." at the end of the string
      const reDangling = new RegExp(`<(?:${names})(?:[^>]*)$`, "gi");
      out = out.replace(reDangling, "");
      // 3) Remove dangling closing tag starts like "</thinking"
      const reDanglingClose = new RegExp(`</(?:${names})(?:[^>]*)$`, "gi");
      out = out.replace(reDanglingClose, "");
      return out;
    };

    const reconstructInlineTag = (tagName, node) => {
      const attrs = getAttrs(node);
      const attrString = Object.entries(attrs)
        .map(([k, v]) => `${k}="${String(v)}"`)
        .join(" ");
      const content =
        node && typeof node["#text"] === "string" ? node["#text"] : "";
      return `<${tagName}${
        attrString ? " " + attrString : ""
      }>${content}</${tagName}>`;
    };

    const toString = (value) => {
      if (value == null) return "";
      if (typeof value === "string") return value;
      return String(value);
    };

    for (const token of tokens) {
      if (typeof token === "string") {
        textBuffer += sanitizeControlTagLeak(token);
        continue;
      }

      // token is an object with a single root key
      const tagName = Object.keys(token)[0];
      const node = token[tagName] || {};

      if (
        tagName === "citation" ||
        tagName === "excel-citation" ||
        tagName === "entity"
      ) {
        // Keep these inline inside text
        textBuffer += reconstructInlineTag(tagName, node);
        continue;
      }

      if (tagName === "IsolationChecklist") {
        flushText();
        const attrs = getAttrs(node);
        // For nested XML structures, we need to preserve the entire node structure
        // not just the #text property
        blocks.push({
          type: "IsolationChecklist",
          attributes: attrs,
          content: node, // Pass the entire node with nested structure
          selfClosing: false,
        });
        continue;
      }

      if (tagName === "file") {
        flushText();
        const attrs = getAttrs(node);
        const content = toString(node["#text"]) || "";
        blocks.push({
          type: "file",
          attributes: {
            url: attrs.url,
            name: attrs.name,
          },
          content,
          selfClosing: false,
        });
        continue;
      }

      if (tagName === "thinking") {
        flushText();
        const content = toString(node["#text"]).trim();
        blocks.push({ type: "thinking", content, selfClosing: false });
        continue;
      }

      if (tagName === "tool_call") {
        flushText();
        const attrs = getAttrs(node);
        let args = attrs.arguments != null ? String(attrs.arguments) : "";
        // Unescape and try JSON.parse
        const unescaped = xmlUnescape(args);
        let parsedArgs = unescaped;
        try {
          parsedArgs = JSON.parse(unescaped);
        } catch (_) {}
        // Optional display helpers from API
        const displayName =
          attrs.display_name != null ? String(attrs.display_name) : undefined;
        const displayArgsRaw =
          attrs.display_arguments != null
            ? String(attrs.display_arguments)
            : undefined;
        const displayArguments =
          displayArgsRaw != null ? xmlUnescape(displayArgsRaw) : undefined;
        blocks.push({
          type: "tool_call",
          attributes: {
            id: attrs.id,
            name: attrs.name,
            arguments: parsedArgs,
            display_name: displayName,
            display_arguments: displayArguments,
          },
          content: null,
          selfClosing: false,
        });
        continue;
      }

      if (tagName === "tool_result") {
        flushText();
        const attrs = getAttrs(node);
        const content = toString(node["#text"]).trim();
        blocks.push({
          type: "tool_result",
          attributes: { id: attrs.id, name: attrs.name },
          content,
          selfClosing: false,
        });
        continue;
      }

      if (
        tagName === "meta_init" ||
        tagName === "meta_final" ||
        tagName === "max_steps_reached"
      ) {
        flushText();
        const attrs = getAttrs(node);
        blocks.push({
          type: tagName,
          attributes: attrs,
          content: null,
          selfClosing: true,
        });
        continue;
      }

      // Fallback: treat as text by reconstructing if possible
      try {
        textBuffer += reconstructInlineTag(tagName, node);
      } catch (_) {
        // last resort append raw text
        const content = toString(node["#text"]) || "";
        textBuffer += content;
      }
    }

    // Special handling: if a <thinking> tag is open without a closing tag,
    // stream its content live into a thinking block and prevent it leaking into text.
    try {
      const lastOpenIdx = text.lastIndexOf("<thinking>");
      const lastCloseIdx = text.lastIndexOf("</thinking>");
      if (lastOpenIdx !== -1 && lastOpenIdx > lastCloseIdx) {
        const rawThinking = text.slice(lastOpenIdx + "<thinking>".length);
        const thinkingContent = sanitizeControlTagLeak(rawThinking).trim();
        if (thinkingContent) {
          // Remove this trailing thinking content from textBuffer if present
          const tailIdx = textBuffer.lastIndexOf(thinkingContent);
          if (
            tailIdx !== -1 &&
            tailIdx + thinkingContent.length === textBuffer.length
          ) {
            textBuffer = textBuffer.slice(0, tailIdx);
          }
          flushText();
          blocks.push({
            type: "thinking",
            content: thinkingContent,
            selfClosing: false,
            status: "streaming",
          });
        }
      }
    } catch (_) {}

    flushText();
    this.blocks = blocks;
    return blocks;
  }
}

export default StreamBlockParser;

/**
 * @typedef {Object} ParsedBlock
 * @property {string} type - Block type: "text", "thinking", "tool_call", "tool_result", "chart", "file", "meta_init", "meta_final", "max_steps_reached"
 * @property {Object} [attributes] - Attributes for tool_call, tool_result, file, and meta blocks
 * @property {Object} [config] - Chart.js configuration object for chart blocks
 * @property {string|null} content - Text content for text and thinking blocks
 * @property {boolean} [selfClosing] - Whether the block is self-closing
 * @property {string} [status] - Status for streaming blocks (e.g., "streaming")
 */
